/**
 * LAUDO DO DISC — a folha que o RH imprime e entrega.
 *
 * O documento é o mesmo que o RH já fazia à mão ("Resultado do Disc — Motorista
 * de Munck — Isaías Gomes da Silva"): faixa verde da SERCAMP no topo, o placar
 * dos quatro fatores, o perfil predominante, a leitura do resultado e, no fim, a
 * avaliação do RH. Aqui ele passa a sair pronto do sistema — o texto automático
 * vem de `leituraDisc()`, que é determinístico e não depende de internet nem de
 * chave de IA.
 *
 * Está em `src/` e é .ts puro (sem JSX) porque isto NÃO é tela: é uma janela de
 * impressão montada como string, do mesmo jeito que o roteiro de entrevista e o
 * plano de ação fazem (ver RoteiroEntrevistaModal.tsx e PlanoAcaoModal.tsx).
 * Ficar fora do componente deixa qualquer lugar do RH imprimir o MESMO laudo —
 * a aba do DISC, a triagem e a entrevista — sem duplicar o desenho da folha.
 *
 * As cores daqui são fixas de propósito. É a única exceção à regra do projeto de
 * só usar as variáveis do tema: a janela de impressão não carrega o styles.css e
 * o papel não tem modo escuro — o verde da marca precisa sair igual sempre.
 */

import {
  ATENCAO_BAIXO,
  AVISO_DISC,
  FATORES,
  FATOR_INFO,
  QUESTOES_DISC,
  TOTAL_QUESTOES,
  faixaDisc,
  leituraDisc,
  type Fator,
  type PlacarDisc,
  type ResultadoDisc,
} from "./disc-modelo";

/** Tudo o que a folha precisa saber — vem da resposta já corrigida no servidor. */
export interface DadosFolhaDisc {
  candidato: string;
  vaga: string | null;
  cargo: string | null;
  email?: string | null;
  telefone?: string | null;
  placar: { D: number; I: number; S: number; C: number };
  perfil: string;
  perfilNome: string;
  /** Parecer do RH ou da IA (texto livre, pode ter várias linhas). */
  parecer?: string | null;
  observacao?: string | null;
  /** ISO — quando o candidato respondeu. */
  respondidoEm?: string | null;
  /** Quem emitiu o laudo (usuário do RH). */
  avaliador?: string | null;
  /** Opcional: anexo com o que foi marcado em cada questão. */
  respostas?: { q: number; alternativa: string }[];
  /**
   * O QUESTIONÁRIO QUE ESTE CANDIDATO RESPONDEU.
   *
   * Vem preenchido quando a vaga tem as suas questões (o DISC por cargo); vazio
   * quer dizer o questionário geral do papel. O laudo precisa dele por dois
   * motivos: o anexo imprime o enunciado ao lado de cada marcação — com a lista
   * errada, a folha mostraria a frase de outro teste ao lado do "12C" — e o
   * TOTAL de questões é o que dá sentido ao placar ("9 de 20") e às faixas.
   */
  questoes?: { n: number; enunciado: string; alternativas: { letra: string; texto: string }[] }[];
  /** De onde veio o questionário — sai impresso no rodapé do laudo. */
  origemQuestoes?: string | null;
}

/* ------------------------------------------------------------------ *
 *  Auxiliares                                                          *
 * ------------------------------------------------------------------ */

/** Escapa o texto que vai para a janela de impressão (é HTML montado à mão). */
function escapar(s: unknown): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * ISO → DD/MM/AAAA.
 *
 * DUAS situações diferentes, e o teste é feito na string INTEIRA:
 *
 * • "2026-09-02" (só a data, sem hora): corta no texto mesmo. Jogar isso em
 *   `new Date()` vira meia-noite UTC e, no fuso do Brasil, o laudo sairia com o
 *   dia anterior — é o mesmo cuidado do `fmt()` do endomarketing.
 * • "2026-09-03T00:30:00Z" (TIMESTAMPTZ, que é o tipo de `respondido_em`):
 *   converter é obrigatório. Fatiar os 10 primeiros caracteres carimbaria 03/09
 *   em quem respondeu às 21h30 do dia 02 no horário de Brasília — o candidato
 *   veria no laudo um dia em que nem sentou para fazer o teste.
 */
function dataBr(iso: string): string {
  const bruto = String(iso ?? "").trim();
  if (!bruto) return "—";
  // Só a data, nada mais: aí sim o recorte é seguro (e é o único caso).
  if (/^\d{4}-\d{2}-\d{2}$/.test(bruto)) return bruto.split("-").reverse().join("/");
  const d = new Date(bruto);
  return Number.isNaN(d.getTime()) ? bruto : d.toLocaleDateString("pt-BR");
}

/** "Dominância (D) e Influência (I)" — enumeração em português. */
function enumerar(itens: string[]): string {
  if (itens.length <= 1) return itens[0] ?? "";
  return `${itens.slice(0, -1).join(", ")} e ${itens[itens.length - 1]}`;
}

/**
 * Reconstrói o resultado corrigido a partir do que foi GRAVADO.
 *
 * O laudo não recalcula o perfil: ele usa o `perfil` que veio do banco ("C/S") e
 * só descobre quais fatores são esses para poder chamar `leituraDisc()`. Se
 * recalculasse pelo placar, um laudo reimpresso amanhã poderia discordar do que
 * está salvo na ficha do candidato. O cálculo pelo placar (mesma ordem de
 * desempate da correção, D → I → S → C) fica como plano B, para perfil vazio ou
 * fora do padrão.
 */
function resultadoDaFolha(dados: DadosFolhaDisc): ResultadoDisc {
  const placar: PlacarDisc = {
    D: Number(dados.placar?.D) || 0,
    I: Number(dados.placar?.I) || 0,
    S: Number(dados.placar?.S) || 0,
    C: Number(dados.placar?.C) || 0,
  };

  const doPerfil = String(dados.perfil ?? "")
    .toUpperCase()
    .split("/")
    .map((p) => p.trim())
    .filter((p): p is Fator => (FATORES as string[]).includes(p));

  const ordenados = [...FATORES].sort((a, b) => placar[b] - placar[a]);
  const primario: Fator = doPerfil[0] ?? ordenados[0]!;
  const provavelSecundario: Fator | null = doPerfil[0] ? (doPerfil[1] ?? null) : ordenados[1]!;
  // Fator zerado não vira "apoio": o teste não sustenta uma leitura de algo que
  // não foi marcado nenhuma vez (mesma regra da correção).
  const secundario =
    provavelSecundario && placar[provavelSecundario] > 0 ? provavelSecundario : null;

  return {
    placar,
    primario,
    secundario,
    perfil: dados.perfil || (secundario ? `${primario}/${secundario}` : primario),
    perfilNome:
      dados.perfilNome ||
      (secundario
        ? `${FATOR_INFO[primario].nome} com ${FATOR_INFO[secundario].nome}`
        : FATOR_INFO[primario].nome),
    // Quantas marcações valeram ponto é exatamente o que o placar soma.
    respondidas: FATORES.reduce((soma, f) => soma + placar[f], 0),
  };
}

/**
 * O segundo fator só vira "fator de apoio" quando tem força real (>= 6).
 *
 * É o MESMO limiar do `leituraDisc()`, e não por coincidência: é ele que decide
 * se as características do segundo fator entram na lista de pontos fortes. Com
 * 2 ou 3 marcações, o laudo dizia "como fator de apoio aparece Estabilidade,
 * trazendo paciência, colaboração..." e, três centímetros abaixo, "S baixo:
 * menor tolerância à rotina" — a mesma página elogiava e alertava sobre o mesmo
 * traço. Abaixo de 6, o fator continua aparecendo, mas na frase dos fatores em
 * faixa baixa, que é o que o teste sustenta.
 */
function fatorDeApoio(res: ResultadoDisc, total: number): Fator | null {
  return res.secundario && faixaDisc(res.placar[res.secundario], total) !== "baixa"
    ? res.secundario
    : null;
}

/**
 * O parágrafo de leitura do resultado — o "o que isso quer dizer" do laudo.
 *
 * Sai das próprias definições do gabarito (FATOR_INFO) para o texto do sistema e
 * o texto do papel do RH nunca divergirem.
 */
function paragrafoLeitura(res: ResultadoDisc, total: number): string {
  const p = res.primario;
  const apoio = fatorDeApoio(res, total);
  const partes = [
    `O comportamento predominante é ${FATOR_INFO[p].nome} (${p}), com ${res.placar[p]} de ` +
      `${total} marcações — faixa ${faixaDisc(res.placar[p], total)} —, o que indica ` +
      `${FATOR_INFO[p].descricao}`,
  ];

  if (apoio) {
    partes.push(
      `Como fator de apoio aparece ${FATOR_INFO[apoio].nome} (${apoio}), com ${res.placar[apoio]} ` +
        `marcação(ões) — faixa ${faixaDisc(res.placar[apoio], total)} —, trazendo ` +
        `${FATOR_INFO[apoio].descricao}`,
    );
  }

  const baixos = FATORES.filter(
    (f) => f !== res.primario && f !== apoio && faixaDisc(res.placar[f], total) === "baixa",
  );
  if (baixos.length > 0) {
    partes.push(
      `${enumerar(baixos.map((f) => `${FATOR_INFO[f].nome} (${f})`))} ` +
        `${baixos.length > 1 ? "aparecem" : "aparece"} em faixa baixa — é o que deve ser ` +
        `confirmado na entrevista e acompanhado na integração.`,
    );
  }

  return partes.join(" ");
}

/** Uma linha do placar: sigla, nome, número, faixa, barra e definição. */
function linhaFator(fator: Fator, valor: number, total: number): string {
  const info = FATOR_INFO[fator];
  // Largura = valor sobre o total de questões. Sem SVG e sem canvas: uma div
  // colorida sobrevive à janela de impressão de qualquer navegador.
  const largura = Math.max(0, Math.min(100, (valor / total) * 100));
  return `<div class="fator">
      <span class="sigla" style="background:${info.cor}">${fator}</span>
      <div class="fator-corpo">
        <div class="fator-topo">
          <span class="fator-nome">${escapar(info.nome)}</span>
          <span class="fator-valor"><strong>${valor}</strong> / ${total}</span>
          <span class="fator-faixa">faixa ${escapar(faixaDisc(valor, total))}</span>
        </div>
        <div class="trilho"><div class="barra" style="width:${largura.toFixed(
          1,
        )}%;background:${info.cor}"></div></div>
        <div class="fator-def">${escapar(info.descricao)}</div>
      </div>
    </div>`;
}

/**
 * Anexo com o que o candidato marcou.
 *
 * Mostra a letra e o texto da alternativa, mas NUNCA o fator que ela soma: o
 * gabarito é material exclusivo do RH (ver src/disc-modelo.ts) e esta folha é
 * arquivada na pasta do candidato e mostrada a ele na devolutiva.
 */
function anexoRespostas(
  respostas: { q: number; alternativa: string }[],
  questoes: DadosFolhaDisc["questoes"],
): string {
  // As questões DESTE teste — as da vaga quando ela tem as suas, senão as 20 do
  // papel. Imprimir a lista errada colocaria, ao lado da marcação do candidato,
  // uma frase que ele nunca leu.
  const doTeste = questoes && questoes.length > 0 ? questoes : QUESTOES_DISC;
  const linhas = respostas
    .slice()
    .sort((a, b) => Number(a.q) - Number(b.q))
    .map((r) => {
      const questao = doTeste.find((x) => x.n === Number(r.q));
      if (!questao) return ""; // resposta de questão que não existe mais no teste
      const letra = String(r.alternativa ?? "").toUpperCase();
      const alt = questao.alternativas.find((a) => a.letra === letra);
      return `<tr>
          <td class="num">${questao.n}</td>
          <td>${escapar(questao.enunciado)}</td>
          <td class="letra">${escapar(letra || "—")}</td>
          <td>${escapar(alt ? alt.texto : "não respondida")}</td>
        </tr>`;
    })
    .join("");

  if (!linhas) return "";
  return `<section class="anexo">
      <h2>Anexo — respostas marcadas</h2>
      <table>
        <thead><tr><th>#</th><th>Questão</th><th>Alt.</th><th>Alternativa marcada</th></tr></thead>
        <tbody>${linhas}</tbody>
      </table>
    </section>`;
}

/* ------------------------------------------------------------------ *
 *  A folha                                                             *
 * ------------------------------------------------------------------ */

/** Monta o laudo inteiro (documento HTML pronto, com o estilo embutido). */
export function montarFolhaDisc(dados: DadosFolhaDisc): string {
  const res = resultadoDaFolha(dados);
  /* Quantas questões este teste tinha. Com o DISC por cargo, "20" deixou de ser
     uma constante: é o tamanho do questionário que o candidato respondeu que dá
     sentido ao placar ("9 de 12") e às faixas do laudo. Sem questionário
     gravado — teste do questionário geral, e todo laudo anterior a esta versão
     — continua valendo o total do papel. */
  const total = dados.questoes && dados.questoes.length > 0 ? dados.questoes.length : TOTAL_QUESTOES;
  const leitura = leituraDisc(res, total);

  const candidato = String(dados.candidato ?? "").trim() || "Candidato";
  // O CARGO vem primeiro: é a função, que é o que o laudo do RH sempre trouxe
  // ("Resultado do Disc — Motorista de Munck — Isaías Gomes da Silva"). O nome da
  // aplicação costuma carregar a obra/o contrato junto ("Motorista de Munck —
  // obra Monte Mor") e só entra quando não houver cargo.
  const funcao = String(dados.cargo ?? "").trim() || String(dados.vaga ?? "").trim();
  const titulo = funcao
    ? `Resultado do Disc — ${funcao} — ${candidato}`
    : `Resultado do Disc — ${candidato}`;

  // SÃO DUAS DATAS DIFERENTES, e misturá-las já enganou o RH: a APLICAÇÃO é
  // quando o candidato respondeu (vem do banco) e a EMISSÃO é agora, na hora de
  // imprimir (isto roda no navegador do RH, não é código de servidor). Um laudo
  // reimpresso meses depois continua "aplicado em" no dia do teste, mas
  // "emitido em" no dia da reimpressão — é o que a assinatura logo abaixo está
  // afirmando. Sem a aplicação registrada, o subtítulo simplesmente não fala em
  // data: melhor faltar do que carimbar hoje como se fosse o dia do teste.
  const dataAplicacao = dados.respondidoEm ? dataBr(dados.respondidoEm) : "";
  const dataEmissao = new Date().toLocaleDateString("pt-BR");

  const contato = [dados.email, dados.telefone].map((x) => String(x ?? "").trim()).filter(Boolean);

  // Quantas questões o placar realmente conta. O RH pode lançar o cartão pela
  // metade (candidato que pulou questão, folha rasurada), e o laudo saía sem
  // dizer nada — quem lê vê "9 de 20" e conclui que o candidato é fraco em
  // Conformidade, quando na verdade cinco questões nunca foram marcadas.
  const respondidas = res.respondidas;
  const incompleto = respondidas < total;

  const subtitulo = [
    "Perfil comportamental DISC",
    // O laudo diz QUAL questionário foi aplicado: um teste escrito para o cargo
    // e o formulário geral do papel não podem sair com a mesma cara na pasta do
    // candidato — é a diferença entre "avaliado na função" e "avaliado no geral".
    dados.origemQuestoes === "IA" || dados.origemQuestoes === "Manual"
      ? "questionário específico da função"
      : "questionário geral",
    dataAplicacao ? `aplicado em ${escapar(dataAplicacao)}` : "",
    `${respondidas} de ${total} questões respondidas`,
    contato.length > 0 ? escapar(contato.join(" · ")) : "",
  ]
    .filter(Boolean)
    .join(" · ");

  const parecer = String(dados.parecer ?? "").trim();
  const observacao = String(dados.observacao ?? "").trim();

  // O que já foi descrito como ponto FORTE não volta como ponto de atenção: sem
  // isto, um teste muito fragmentado (em que até o fator predominante fica em
  // faixa baixa) lista "Muito atento a regras, procedimentos e normas" e, três
  // linhas abaixo, "C baixo: menor apego a detalhe" — sobre o mesmo fator, na
  // mesma página. Ver fatorDeApoio() para o caso do segundo fator.
  // ... mas só vale para o fator que o laudo realmente descreveu como forte: num
  // teste fragmentado o "predominante" pode ter 4 de 20, e aí o alerta de fator
  // baixo é justamente a informação que o RH precisa ler — some-lo seria pior do
  // que a repetição que este filtro veio evitar. O limiar é o mesmo de
  // leituraDisc (6 = faixa moderada para cima).
  const apoio = fatorDeApoio(res, total);
  const fortes: Fator[] = (apoio ? [res.primario, apoio] : [res.primario]).filter(
    (f) => faixaDisc(res.placar[f], total) !== "baixa",
  );

  const caracteristicas = leitura.caracteristicas.map((c) => `<li>${escapar(c)}</li>`).join("");
  const atencao = leitura.atencao
    .filter((a) => !fortes.some((f) => a === ATENCAO_BAIXO[f]))
    .map((a) => `<li>${escapar(a)}</li>`)
    .join("");

  return `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
    <title>${escapar(titulo)}</title>
    <style>
      *{box-sizing:border-box}
      body{font-family:system-ui,Segoe UI,sans-serif;color:#13241b;margin:26px;font-size:13px;line-height:1.5}

      /* ---- Faixa verde da SERCAMP ----
         Tudo em CSS, sem imagem: a janela de impressão abre em branco e um <img>
         de arquivo externo pode simplesmente não carregar a tempo — o cabeçalho
         sumiria, que é justamente a parte que o RH pediu para ter. O
         print-color-adjust obriga o navegador a IMPRIMIR o fundo verde (por
         padrão ele descarta fundos para economizar tinta). */
      .cab{position:relative;overflow:hidden;border-radius:6px;color:#fff;
        min-height:78px;padding:16px 22px 16px 138px;
        display:flex;flex-direction:column;justify-content:center;
        background:linear-gradient(135deg,#0f4b2c 0%,#14472e 60%,#0c7a3f 100%);
        -webkit-print-color-adjust:exact;print-color-adjust:exact}
      .cab-listras{position:absolute;left:0;top:0;bottom:0;width:118px;
        background:repeating-linear-gradient(135deg,rgba(255,255,255,.30) 0 6px,rgba(255,255,255,0) 6px 15px);
        -webkit-print-color-adjust:exact;print-color-adjust:exact}
      .cab-nome{font-size:24px;font-weight:800;letter-spacing:.02em;line-height:1.15}
      .cab-slogan{font-size:11.5px;letter-spacing:.06em;text-transform:uppercase;opacity:.92;margin-top:3px}

      h1{font-size:17px;margin:20px 0 2px;text-align:center}
      .sub{text-align:center;color:#5e7268;font-size:11.5px;margin:0 0 16px}
      h2{font-size:12.5px;margin:18px 0 6px;color:#0c7a3f;text-transform:uppercase;letter-spacing:.05em}
      p{margin:0 0 8px}
      .intro{font-size:13.5px;margin:0 0 10px}

      /* ---- Placar ---- */
      .fator{display:flex;gap:10px;align-items:flex-start;margin:0 0 9px}
      .sigla{flex:0 0 26px;height:26px;border-radius:6px;color:#fff;font-weight:800;font-size:14px;
        display:flex;align-items:center;justify-content:center;
        -webkit-print-color-adjust:exact;print-color-adjust:exact}
      .fator-corpo{flex:1;min-width:0}
      .fator-topo{display:flex;align-items:baseline;gap:8px;flex-wrap:wrap}
      .fator-nome{font-weight:700;font-size:13px}
      .fator-valor{font-size:13px}
      .fator-valor strong{font-size:15px}
      .fator-faixa{font-size:10.5px;color:#5e7268;text-transform:uppercase;letter-spacing:.04em}
      .trilho{height:9px;border-radius:5px;background:#eaf1ed;margin:4px 0 3px;overflow:hidden;
        -webkit-print-color-adjust:exact;print-color-adjust:exact}
      .barra{height:100%;border-radius:5px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
      .fator-def{font-size:11.5px;color:#5e7268}

      .perfil{border:1px solid #cbd8d0;border-left:4px solid #0c7a3f;border-radius:6px;
        padding:9px 12px;margin:12px 0 14px;font-size:13.5px;background:#f4f9f6;
        -webkit-print-color-adjust:exact;print-color-adjust:exact}
      .perfil strong{font-size:14.5px}
      .perfil-ressalva{font-size:11.5px;color:#5e7268}

      ul{margin:0 0 8px;padding-left:18px}
      li{margin:0 0 3px}
      .txt{white-space:pre-wrap;border:1px solid #cbd8d0;border-radius:6px;padding:9px 11px;min-height:44px}
      .pauta{border-bottom:1px dotted #9db3a6;height:17px;margin-top:9px}

      /* ---- Tarja de teste incompleto ----
         Discreta de propósito: é um aviso de leitura, não um carimbo de
         reprovação. Fica no âmbar do fator I para não competir com o verde da
         marca, e também pede o print-color-adjust — um aviso que some na
         impressão não serve para nada. */
      .tarja{border:1px solid #e6d4a6;border-left:3px solid #e9a13b;border-radius:6px;
        background:#fdf7e8;color:#6b5518;font-size:11.5px;padding:7px 10px;margin:0 0 12px;
        -webkit-print-color-adjust:exact;print-color-adjust:exact}

      /* ---- Anexo ---- */
      table{width:100%;border-collapse:collapse;font-size:11.5px}
      th,td{border:1px solid #cbd8d0;padding:5px 7px;text-align:left;vertical-align:top}
      th{background:#eaf5ee;font-size:10.5px;text-transform:uppercase;letter-spacing:.04em;
        -webkit-print-color-adjust:exact;print-color-adjust:exact}
      .num{width:26px;text-align:center;color:#5e7268}
      .letra{width:34px;text-align:center;font-weight:700}

      /* ---- Rodapé ---- */
      .aviso{font-size:11px;color:#5e7268;border-top:1px solid #cbd8d0;
        padding-top:8px;margin-top:18px;font-style:italic}
      .emissao{font-size:11px;color:#5e7268;margin-top:4px}
      .ass{margin-top:46px;display:flex;justify-content:flex-end}
      .ass div{width:280px;border-top:1px solid #333;padding-top:6px;font-size:11.5px;text-align:center}

      @media print{
        body{margin:12mm}
        /* O laudo é de uma página só; o que não pode é quebrar no meio de um bloco. */
        h2{page-break-after:avoid}
        .cab,.fator,.perfil,.bloco,.tarja,.ass{page-break-inside:avoid}
        tr{page-break-inside:avoid}
        /* O anexo é opcional, é a ÚLTIMA coisa do documento (depois do aviso, da
           linha de emissão e da assinatura) e começa em página nova. Enquanto
           ele era emitido antes desse fecho, a quebra de página empurrava o
           aviso obrigatório e a assinatura para depois da tabela de respostas —
           o laudo terminava sem assinatura na primeira página. */
        .anexo{page-break-before:always}
      }
    </style></head><body>

    <header class="cab">
      <div class="cab-listras"></div>
      <div class="cab-nome">Sercamp Serviços</div>
      <div class="cab-slogan">Completa em Manutenção e Prevenção Elétrica</div>
    </header>

    <h1>${escapar(titulo)}</h1>
    <p class="sub">${subtitulo}</p>

    ${
      incompleto
        ? `<p class="tarja"><strong>Teste incompleto:</strong> ${respondidas} das ${total} questões foram marcadas. O placar abaixo considera só o que foi respondido — as questões em branco não pontuam para nenhum fator, e por isso os números somam ${respondidas}. Leia o resultado como indicativo e confirme na entrevista.</p>`
        : ""
    }

    <section class="bloco">
      <p class="intro">${
        funcao
          ? `Para o cargo de <strong>${escapar(funcao)}</strong>, o resultado foi:`
          : "O resultado foi:"
      }</p>
      ${FATORES.map((f) => linhaFator(f, res.placar[f], total)).join("")}
      <div class="perfil">Perfil predominante: <strong>${escapar(res.perfil)} — ${escapar(
        res.perfilNome,
      )}</strong>${
        // O nome do perfil é sempre "maior/segundo maior" (é assim que o RH
        // escreve no laudo), mas o segundo fator pode estar em faixa baixa — e aí
        // a caixa em destaque contradizia os "pontos de atenção" logo abaixo, na
        // mesma página. A ressalva sai junto do nome, não no lugar dele.
        res.secundario && res.placar[res.secundario] <= 5
          ? `<span class="perfil-ressalva"> — ${escapar(
              FATOR_INFO[res.secundario].nome,
            )} aparece em faixa baixa (${res.placar[res.secundario]} de 20); leia os pontos de atenção.</span>`
          : ""
      }</div>
      <p>${escapar(paragrafoLeitura(res, total))}</p>
    </section>

    <section class="bloco">
      <h2>${
        // Num teste sem fator dominante (o predominante ficou em faixa baixa),
        // chamar a lista de "principais características" afirmaria mais do que o
        // resultado sustenta — o mesmo laudo lista, abaixo, o alerta de fator
        // baixo desse mesmo fator.
        res.placar[res.primario] >= 6
          ? "Principais características"
          : "Tendências observadas (sem fator dominante)"
      }</h2>
      <ul>${caracteristicas}</ul>
    </section>

    ${
      atencao
        ? `<section class="bloco">
      <h2>Pontos de atenção</h2>
      <ul>${atencao}</ul>
    </section>`
        : ""
    }

    <section class="bloco">
      <h2>Minha avaliação para RH</h2>
      ${
        parecer
          ? // O parecer do RH (ou o texto que a IA sugeriu e o RH aprovou) sai
            // como foi escrito, com as quebras de linha preservadas.
            `<div class="txt">${escapar(parecer)}</div>`
          : // Sem parecer escrito, a folha sai com linhas: o RH costuma imprimir
            // antes da devolutiva e escrever a conclusão à mão.
            `<div class="pauta"></div><div class="pauta"></div><div class="pauta"></div>`
      }
      ${observacao ? `<p class="emissao">Observação: ${escapar(observacao)}</p>` : ""}
    </section>

    <p class="aviso">${escapar(AVISO_DISC)}</p>
    <p class="emissao">${
      dados.avaliador ? `Parecer de ${escapar(dados.avaliador)} · ` : ""
    }emitido em ${escapar(dataEmissao)}.</p>

    <div class="ass"><div>Responsável pelo RH</div></div>

    ${
      // O anexo fecha o documento, DEPOIS da assinatura: ele começa em página
      // nova (ver @media print) e, se viesse antes, levaria o aviso e a
      // assinatura junto para a segunda página.
      dados.respostas && dados.respostas.length > 0
        ? anexoRespostas(dados.respostas, dados.questoes)
        : ""
    }
    </body></html>`;
}

/**
 * Abre a janela de impressão com o laudo.
 *
 * Devolve `null` quando deu certo e a MENSAGEM DE ERRO quando o navegador
 * bloqueou o pop-up — quem chama mostra o aviso na tela (mesmo tratamento do
 * RoteiroEntrevistaModal). Um `return` mudo faria o RH clicar em "Imprimir",
 * não acontecer nada e concluir que o sistema está quebrado.
 */
export function imprimirFolhaDisc(dados: DadosFolhaDisc): string | null {
  const janela = window.open("", "_blank");
  if (!janela) {
    return "O navegador bloqueou a janela de impressão. Libere os pop-ups deste site.";
  }
  janela.document.write(montarFolhaDisc(dados));
  janela.document.close();
  janela.focus();
  janela.print();
  return null;
}
